import React, { useEffect, useMemo, useState } from 'react';
import { Lightbulb, Plus, Check } from 'lucide-react';

export const GARMENT_INSIGHT_AUDIENCES = [
  { key: 'women', label: 'Women', category: 'women-clothing', tone: 'pink' },
  { key: 'men', label: 'Men', category: 'men-clothing', tone: 'blue' },
  { key: 'kids_boy', label: 'Kids Boy', category: 'kids-boy', tone: 'sky' },
  { key: 'kids_girl', label: 'Kids Girl', category: 'kids-girl', tone: 'violet' },
];

const TONE_CLASSES = {
  pink: {
    tab: 'bg-pink-100 text-pink-800 border-pink-200',
    tabIdle: 'bg-white text-slate-600 border-slate-200 hover:bg-pink-50',
    chip: 'bg-pink-50 border-pink-100 text-pink-900',
    rank: 'bg-pink-600 text-white',
  },
  blue: {
    tab: 'bg-blue-100 text-blue-800 border-blue-200',
    tabIdle: 'bg-white text-slate-600 border-slate-200 hover:bg-blue-50',
    chip: 'bg-blue-50 border-blue-100 text-blue-900',
    rank: 'bg-blue-600 text-white',
  },
  sky: {
    tab: 'bg-sky-100 text-sky-800 border-sky-200',
    tabIdle: 'bg-white text-slate-600 border-slate-200 hover:bg-sky-50',
    chip: 'bg-sky-50 border-sky-100 text-sky-900',
    rank: 'bg-sky-600 text-white',
  },
  violet: {
    tab: 'bg-violet-100 text-violet-800 border-violet-200',
    tabIdle: 'bg-white text-slate-600 border-slate-200 hover:bg-violet-50',
    chip: 'bg-violet-50 border-violet-100 text-violet-900',
    rank: 'bg-violet-600 text-white',
  },
};

/**
 * Normalize fabric analysis into top-5 garment insights per audience.
 * Supports new `garment_type_insights` and legacy men/women recommendation arrays.
 */
export function getGarmentTypeInsights(fabricAnalysis) {
  if (!fabricAnalysis || typeof fabricAnalysis !== 'object') {
    return { women: [], men: [], kids_boy: [], kids_girl: [] };
  }

  const insights = fabricAnalysis.garment_type_insights || {};
  const result = {};

  for (const audience of GARMENT_INSIGHT_AUDIENCES) {
    let list = Array.isArray(insights[audience.key]) ? insights[audience.key] : [];

    if (list.length === 0 && (audience.key === 'women' || audience.key === 'men')) {
      const legacyKey =
        audience.key === 'women' ? 'women_garment_recommendations' : 'men_garment_recommendations';
      const legacy = fabricAnalysis?.[legacyKey]?.best_suited || [];
      list = legacy.map((item, idx) => ({
        rank: idx + 1,
        type_value: String(item.garment || '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-'),
        type_label: item.garment,
        reason: item.reason || '',
        category: audience.category,
      }));
    }

    result[audience.key] = list
      .slice(0, 5)
      .map((item, idx) => ({
        rank: item.rank || idx + 1,
        type_value: item.type_value || item.value || '',
        type_label: item.type_label || item.label || item.garment || `Option ${idx + 1}`,
        reason: item.reason || '',
        category: item.category || audience.category,
      }))
      .filter((item) => item.type_label);
  }

  return result;
}

export function hasGarmentTypeInsights(fabricAnalysis) {
  const insights = getGarmentTypeInsights(fabricAnalysis);
  return GARMENT_INSIGHT_AUDIENCES.some((a) => (insights[a.key] || []).length > 0);
}

/**
 * Insights panel: top 5 garment types for women / men / kids.
 */
const FabricGarmentInsights = ({
  fabricAnalysis,
  selectedCategoryValue,
  selectedTypes = [],
  onAddType,
  compact = false,
  className = '',
}) => {
  const insights = useMemo(() => getGarmentTypeInsights(fabricAnalysis), [fabricAnalysis]);

  const defaultAudience = useMemo(() => {
    if (selectedCategoryValue === 'men-clothing') return 'men';
    if (selectedCategoryValue === 'kids-boy') return 'kids_boy';
    if (selectedCategoryValue === 'kids-girl') return 'kids_girl';
    if (selectedCategoryValue === 'women-clothing') return 'women';
    const firstWithData = GARMENT_INSIGHT_AUDIENCES.find((a) => (insights[a.key] || []).length > 0);
    return firstWithData?.key || 'women';
  }, [selectedCategoryValue, insights]);

  const [activeAudience, setActiveAudience] = useState(defaultAudience);

  useEffect(() => {
    setActiveAudience(defaultAudience);
  }, [defaultAudience]);

  const activeMeta = GARMENT_INSIGHT_AUDIENCES.find((a) => a.key === activeAudience) || GARMENT_INSIGHT_AUDIENCES[0];
  const tone = TONE_CLASSES[activeMeta.tone];
  const activeList = insights[activeAudience] || [];

  if (!hasGarmentTypeInsights(fabricAnalysis)) return null;

  const isAdded = (item) =>
    selectedTypes.some(
      (t) =>
        t.value === item.type_value ||
        t.finalValue === item.type_value ||
        t.label === item.type_label ||
        t.finalLabel === item.type_label
    );

  return (
    <div className={`rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/80 via-white to-indigo-50/60 ${compact ? 'p-3' : 'p-3 sm:p-4'} ${className}`}>
      <div className="flex items-start gap-2 mb-3">
        <Lightbulb size={16} className="text-amber-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">Top garment types for this fabric</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            AI ranked the best 5 types for women, men, and kids based on fabric properties.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {GARMENT_INSIGHT_AUDIENCES.map((audience) => {
          const count = (insights[audience.key] || []).length;
          if (!count) return null;
          const isActive = activeAudience === audience.key;
          const audienceTone = TONE_CLASSES[audience.tone];
          return (
            <button
              key={audience.key}
              type="button"
              onClick={() => setActiveAudience(audience.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                isActive ? audienceTone.tab : audienceTone.tabIdle
              }`}
            >
              {audience.label}
              <span className="opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {activeList.map((item) => {
          const added = isAdded(item);
          return (
            <div
              key={`${activeAudience}-${item.rank}-${item.type_value || item.type_label}`}
              className={`flex items-start gap-2.5 rounded-lg border px-2.5 py-2 ${tone.chip}`}
            >
              <span
                className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${tone.rank}`}
              >
                {item.rank}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 truncate">{item.type_label}</p>
                {item.reason && (
                  <p className={`text-[11px] text-slate-600 mt-0.5 ${compact ? 'line-clamp-1' : 'line-clamp-2'}`}>
                    {item.reason}
                  </p>
                )}
              </div>
              {typeof onAddType === 'function' && (
                <button
                  type="button"
                  disabled={added || selectedTypes.length >= 5}
                  onClick={() => onAddType(item, activeMeta)}
                  className={`shrink-0 inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition ${
                    added || selectedTypes.length >= 5
                      ? 'bg-white/70 text-slate-400 cursor-not-allowed border border-slate-200'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'
                  }`}
                  title={added ? 'Already added' : 'Add as product type'}
                >
                  {added ? <Check size={12} /> : <Plus size={12} />}
                  {added ? 'Added' : 'Add'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FabricGarmentInsights;
